/**
 * /cc-command-matrix ルートの URL search params 同期ロジック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business AI 規定コマンド利用状況 dashboard の URL search params を sparse 表現で復元・正規化する pure functions。default 値は URL に出さず、不正な値は default にフォールバックする
 * @graph-connects none
 */

import type { CcCommandMatrixAxis, CcEventsRange } from '../types/api.js';

/** @graph-connects none */
export const CC_COMMAND_MATRIX_AXIS_VALUES = [
  'member',
  'project',
] as const satisfies readonly CcCommandMatrixAxis[];

/** @graph-connects none */
export type CcCommandMatrixRange = Exclude<CcEventsRange, 'custom'>;

/** @graph-connects none */
export const CC_COMMAND_MATRIX_RANGE_VALUES = [
  '7d',
  '14d',
  '30d',
  '90d',
  '180d',
  '365d',
] as const satisfies readonly CcCommandMatrixRange[];

/** @graph-connects none */
export interface CcCommandMatrixSearch {
  axis?: CcCommandMatrixAxis;
  range?: CcCommandMatrixRange;
  team?: string;
  includeCortex?: boolean;
  hideNoData?: boolean;
  /** PJ 軸で完了済み PJ (ticketStatus=完了) も表示する。既定 false = 非表示 */
  showCompleted?: boolean;
}

/** @graph-connects none */
export const CC_COMMAND_MATRIX_SEARCH_DEFAULTS = {
  axis: 'member' as CcCommandMatrixAxis,
  range: '30d' as CcCommandMatrixRange,
  includeCortex: false,
  hideNoData: false,
  showCompleted: false,
};

/**
 * TanStack Router は boolean を JSON として URL に出すが、手入力や旧リンクでは文字列 'true' も来る。
 * どちらも true として読む。それ以外は未指定 (= default false)。
 * @graph-connects none
 */
function parseFlag(raw: unknown): true | undefined {
  return raw === true || raw === 'true' ? true : undefined;
}

/**
 * URL search を復元する。不正値は黙って undefined にし、既定値と一致する値も undefined に潰す。
 * @graph-connects none
 */
export function parseCcCommandMatrixSearch(raw: Record<string, unknown>): CcCommandMatrixSearch {
  const rawAxis = raw['axis'];
  const axis =
    typeof rawAxis === 'string' &&
    (CC_COMMAND_MATRIX_AXIS_VALUES as readonly string[]).includes(rawAxis) &&
    rawAxis !== CC_COMMAND_MATRIX_SEARCH_DEFAULTS.axis
      ? (rawAxis as CcCommandMatrixAxis)
      : undefined;
  const rawRange = raw['range'];
  const range =
    typeof rawRange === 'string' &&
    (CC_COMMAND_MATRIX_RANGE_VALUES as readonly string[]).includes(rawRange) &&
    rawRange !== CC_COMMAND_MATRIX_SEARCH_DEFAULTS.range
      ? (rawRange as CcCommandMatrixRange)
      : undefined;
  const team = typeof raw['team'] === 'string' && raw['team'].length > 0 ? raw['team'] : undefined;
  return {
    axis,
    range,
    team,
    includeCortex: parseFlag(raw['includeCortex']),
    hideNoData: parseFlag(raw['hideNoData']),
    showCompleted: parseFlag(raw['showCompleted']),
  };
}

/**
 * sparse search を完全形に戻して default を埋めた値を取り出す。
 * @graph-connects none
 */
export function resolveCcCommandMatrixSearch(search: CcCommandMatrixSearch): {
  axis: CcCommandMatrixAxis;
  range: CcCommandMatrixRange;
  team: string | undefined;
  includeCortex: boolean;
  hideNoData: boolean;
  showCompleted: boolean;
} {
  return {
    axis: search.axis ?? CC_COMMAND_MATRIX_SEARCH_DEFAULTS.axis,
    range: search.range ?? CC_COMMAND_MATRIX_SEARCH_DEFAULTS.range,
    team: search.team,
    includeCortex: search.includeCortex ?? CC_COMMAND_MATRIX_SEARCH_DEFAULTS.includeCortex,
    hideNoData: search.hideNoData ?? CC_COMMAND_MATRIX_SEARCH_DEFAULTS.hideNoData,
    showCompleted: search.showCompleted ?? CC_COMMAND_MATRIX_SEARCH_DEFAULTS.showCompleted,
  };
}

/**
 * CcCommandMatrixSearch を URL search の sparse 表現に変換する。default 値は出さない。
 * hideNoData は人軸専用、showCompleted は PJ 軸専用なので、他方の軸では URL に残さない。
 * @graph-connects none
 */
export function stripCcCommandMatrixSearchDefaults(
  search: CcCommandMatrixSearch,
): CcCommandMatrixSearch {
  const resolved = resolveCcCommandMatrixSearch(search);
  const out: CcCommandMatrixSearch = {};
  if (resolved.axis !== CC_COMMAND_MATRIX_SEARCH_DEFAULTS.axis) out.axis = resolved.axis;
  if (resolved.range !== CC_COMMAND_MATRIX_SEARCH_DEFAULTS.range) out.range = resolved.range;
  if (resolved.team) out.team = resolved.team;
  if (resolved.includeCortex) out.includeCortex = true;
  if (resolved.hideNoData && resolved.axis === 'member') out.hideNoData = true;
  if (resolved.showCompleted && resolved.axis === 'project') out.showCompleted = true;
  return out;
}
